/**
 * Slide-out drawer showing the full detail of a single observation.
 * Opens when a user clicks a row in FullDataTable.
 *
 * Layout:
 *   - Header: site, date, observer, provider, score badge
 *   - Per-subscale accordion: name, average, items with scores + notes
 *   - Per-item expandable: indicator-level Y/N/NA responses
 */

import { useState } from 'react';
import { X, ChevronDown, ChevronRight, MapPin, Clock, Users, FileText, Download } from 'lucide-react';
import type { DashboardObservation, DetailSubscale, DetailItem } from '@/lib/dashboard/types';
import { SUBSCALE_META } from '@/lib/dashboard/types';

function scoreBadgeColor(score: number | null): string {
  if (score === null) return 'bg-gray-100 text-gray-500';
  if (score >= 5) return 'bg-green-50 text-green-700';
  if (score >= 3) return 'bg-yellow-50 text-yellow-700';
  return 'bg-red-50 text-red-700';
}

function ScoreBadge({ score, size = 'sm' }: { score: number | null; size?: 'sm' | 'lg' }) {
  const cls = size === 'lg' ? 'px-3 py-1 text-lg font-bold' : 'px-2 py-0.5 text-xs font-semibold';
  return (
    <span className={`inline-flex rounded-full ${cls} ${scoreBadgeColor(score)}`}>
      {score !== null ? score.toFixed(2) : '—'}
    </span>
  );
}

function IndicatorResponseBadge({ response }: { response: 'Y' | 'N' | 'NA' }) {
  const cls = {
    Y: 'bg-green-100 text-green-800',
    N: 'bg-red-100 text-red-800',
    NA: 'bg-gray-100 text-gray-600',
  }[response];
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-bold ${cls}`}>
      {response}
    </span>
  );
}

function ItemRow({
  item,
  note,
  responses,
}: {
  item: DetailItem;
  note: string;
  responses: Record<string, 'Y' | 'N' | 'NA'>;
}) {
  const [open, setOpen] = useState(false);

  // Find indicator responses for this item (they start with the item id)
  const itemIndicators = Object.entries(responses)
    .filter(([key]) => key.startsWith(item.id + '-'))
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="border-b last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-muted/50"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="w-6 shrink-0 text-xs text-muted-foreground">#{item.number}</span>
        <span className="flex-1 font-medium">{item.name}</span>
        {item.itemNa ? (
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">NA</span>
        ) : (
          <ScoreBadge score={item.score} />
        )}
      </button>

      {open && (
        <div className="bg-muted/30 px-4 pb-3 pt-1">
          {note && (
            <div className="mb-2 flex items-start gap-2 rounded-md border bg-background px-3 py-2 text-xs">
              <FileText className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">{note}</span>
            </div>
          )}

          {itemIndicators.length > 0 && (
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {itemIndicators.map(([id, resp]) => {
                // Extract level and number from indicator id like FCCERS3-S1-I1-L3-2
                const parts = id.split('-');
                const level = parts[parts.length - 2]?.replace('L', '');
                const num = parts[parts.length - 1];
                return (
                  <div
                    key={id}
                    className="flex items-center gap-2 rounded border bg-background px-2 py-1 text-xs"
                  >
                    <span className="w-12 shrink-0 font-mono text-muted-foreground">
                      L{level}.{num}
                    </span>
                    <IndicatorResponseBadge response={resp} />
                  </div>
                );
              })}
            </div>
          )}

          {itemIndicators.length === 0 && !note && (
            <p className="text-xs text-muted-foreground">No indicator detail available.</p>
          )}
        </div>
      )}
    </div>
  );
}

function SubscaleSection({
  subscale,
  detail,
}: {
  subscale: DetailSubscale;
  detail: DashboardObservation['detail'];
}) {
  const [open, setOpen] = useState(false);
  const meta = SUBSCALE_META.find((s) => subscale.id.endsWith(s.id.replace('S', 'S')));
  const color = meta?.color ?? '#64748b';

  return (
    <div className="rounded-lg border">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50"
      >
        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="flex-1 font-medium">{subscale.name}</span>
        <ScoreBadge score={subscale.average} />
        <span className="text-xs text-muted-foreground">
          {subscale.items.length} items
        </span>
      </button>

      {open && (
        <div className="border-t">
          {subscale.items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              note={detail?.itemNotes[item.id] ?? ''}
              responses={detail?.responses ?? {}}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ObservationDrawer({
  observation,
  onClose,
}: {
  observation: DashboardObservation;
  onClose: () => void;
}) {
  const o = observation;
  const detail = o.detail;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-background shadow-2xl">
        {/* Header */}
        <div className="border-b bg-dece-navy px-6 py-4 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold">{o.siteName}</h2>
              <p className="mt-0.5 text-sm text-white/80">{o.providerName}</p>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1 hover:bg-white/20"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span>{o.observationDate}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {o.startTime} – {o.endTime}
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {o.childrenPresent} children
            </span>
          </div>

          {detail?.siteAddress && (
            <div className="mt-1.5 flex items-center gap-1 text-xs text-white/60">
              <MapPin className="h-3 w-3" />
              {detail.siteAddress}
            </div>
          )}

          <div className="mt-3 flex items-center gap-3">
            <div className="rounded-md bg-white/20 px-3 py-1">
              <span className="text-xs text-white/70">Total Score</span>
              <p className="text-xl font-bold">
                {o.totalAverage !== null ? o.totalAverage.toFixed(2) : '—'}
              </p>
            </div>
            <div className="rounded-md bg-white/20 px-3 py-1">
              <span className="text-xs text-white/70">Observer</span>
              <p className="text-sm font-medium">{o.observerName}</p>
            </div>
            <div className="rounded-md bg-white/20 px-3 py-1">
              <span className="text-xs text-white/70">Items</span>
              <p className="text-sm font-medium">
                {o.scoredItems} scored · {o.naItems} NA
              </p>
            </div>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {o.notes && (
            <div className="mb-4 rounded-md border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">General Notes: </span>
              {o.notes}
            </div>
          )}

          {o.ageGroupsPresent && (
            <div className="mb-4 flex flex-wrap gap-1.5">
              {o.ageGroupsPresent.split(', ').map((ag) => (
                <span
                  key={ag}
                  className="rounded-full border bg-background px-2.5 py-0.5 text-xs font-medium"
                >
                  {ag}
                </span>
              ))}
            </div>
          )}

          {detail ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Subscale Breakdown
              </h3>
              {detail.subscales.map((sub) => (
                <SubscaleSection key={sub.id} subscale={sub} detail={detail} />
              ))}
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
              <FileText className="h-8 w-8" />
              <p className="text-sm font-medium">No detailed data available</p>
              <p className="text-xs">
                This observation was submitted before detail tracking was enabled.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-6 py-3">
          <span className="text-xs text-muted-foreground">
            Record {o.recordId} · Exported {new Date(o.exportedAt).toLocaleString()}
          </span>
          <button
            onClick={() => {
              const payload = {
                recordId: o.recordId,
                exportedAt: o.exportedAt,
                metadata: {
                  observationDate: o.observationDate,
                  siteName: o.siteName,
                  providerName: o.providerName,
                  siteAddress: detail?.siteAddress ?? '',
                  observerName: o.observerName,
                  observerEmail: o.observerEmail,
                  childrenPresent: o.childrenPresent,
                  ageGroupsPresent: o.ageGroupsPresent,
                  startTime: o.startTime,
                  endTime: o.endTime,
                  notes: o.notes,
                },
                scores: {
                  totalAverage: o.totalAverage,
                  scoredItems: o.scoredItems,
                  naItems: o.naItems,
                  subscaleAverages: o.subscaleAverages,
                  subscales: detail?.subscales ?? [],
                },
                responses: detail?.responses ?? {},
                itemNa: detail?.itemNa ?? {},
                itemNotes: detail?.itemNotes ?? {},
              };
              const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              const site = o.siteName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
              a.download = `fccers-${site}-${o.observationDate}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Download className="h-3.5 w-3.5" />
            Export JSON
          </button>
        </div>
      </div>
    </>
  );
}
